// The first walkthrough: the steps of "Around Tables" (help/tours/tables.ts),
// each with the card's own words, so the card ships with one real run on any
// job. The steps are copied, not imported: this folder imports types only.
// It opens a folder and a table, shows the chart, opens Edit Spec, adds a
// variable to the rows, moves it, runs the preview and ends on Discard. It
// never saves.

import type { Walkthrough } from './types'

export const aroundTablesCard: Walkthrough = {
  slug: 'around-tables-card',
  title: 'Around Tables',
  summary: 'Opens a table on this job, shows it as a chart, then changes its rows in Edit Spec and shows the result as a preview. Nothing is saved.',
  added: '2026-10-20',
  steps: [
    {
      page: 'tables',
      feature: 'tables/table-of-contents',
      action: { tool: 'expand_toc_folder', args: { folder: { $pick: 'first_folder' } }, label: 'Open the first folder of shared tables' },
      say: 'This is the Tables tree: every saved table of the job, in folders. I opened the first shared folder that holds a table.',
    },
    {
      page: 'tables',
      feature: 'tables/reading-a-table',
      action: { tool: 'open_toc_table', args: { table: { $pick: 'first_table_in_picked_folder' } }, label: 'Open the first table in it' },
      say: 'This is the table: row labels down the side, column labels across the top. Click a cell to see its figures.',
    },
    {
      page: 'tables',
      feature: 'tables/quick-filter',
      control: 'Quick Filter',
      say: 'Quick Filter limits this table to the people a filter picks, without changing the saved table. Try it yourself after the walkthrough.',
    },
    {
      page: 'tables',
      feature: 'tables/chart-view',
      action: { tool: 'show_chart_view', label: 'Show it as a chart' },
      say: 'The same table as a chart. You can change the chart type and what it plots.',
    },
    {
      page: 'tables',
      feature: 'tables/quick-settings',
      action: { tool: 'show_table_view', label: 'Show it as a table again' },
      control: 'Quick Settings',
      say: 'Back to the table. Quick Settings holds every setting for how the table is worked out and shown.',
    },
    {
      page: 'tables',
      feature: 'tables/edit-spec-and-new-table',
      action: { tool: 'open_edit_spec', label: 'Open Edit Spec for this table' },
      say: 'Edit Spec opens this table in the spec editor, to change its rows, columns, filter and weight.',
    },
    {
      route: 'editspec',
      feature: 'tables/edit-spec-page',
      action: { tool: 'add_variable_to_axis', args: { variable: { $pick: 'first_unused_variable' }, axis: 'side' }, label: 'Add a variable to the rows' },
      say: 'I added a variable the table does not use yet to the rows.',
    },
    {
      route: 'editspec',
      feature: 'tables/edit-spec-page',
      action: { tool: 'move_axis_item', args: { axis: 'side', item: { $pick: 'last_added_axis_item' }, direction: 'top' }, label: 'Move it to the top' },
      say: 'I moved it to the top of the rows.',
    },
    {
      route: 'editspec',
      feature: 'tables/edit-spec-page',
      action: { tool: 'run_spec', label: 'Run the spec and see the preview' },
      say: 'Run Spec shows the result on Tables as a preview. Nothing is saved.',
    },
    {
      route: 'tables',
      feature: 'tables/unsaved-changes',
      control: 'Discard',
      say: 'This is a preview. Discard puts the table back as it was; Save Table would keep it. A walkthrough never saves for you.',
    },
  ],
}
